import { createContext, useContext, ReactNode, useMemo } from 'react';
import { useFormik, FormikProvider } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';
import { typesOfSupportData } from 'store/clients/clientsSlice';
import { useAppSelector } from 'store/hooks';

import { validationSchema, NoteFormValues, sortTypesOfSupportValues } from '../utils/validation';

export type NoteFormContextValue = {
  clientId: string;
  note?: ClientProfileContentItem;
  disabled: boolean;
  isManagerReview: boolean;
};

const NoteFormContext = createContext<NoteFormContextValue | null>(null);

export const useNoteFormContext = () => {
  const context = useContext(NoteFormContext);
  if (!context) {
    throw new Error('useNoteFormContext must be used within a NoteFormProvider');
  }
  return context;
};

type NoteFormProviderProps = {
  children: ReactNode;
  clientId: string;
  note?: ClientProfileContentItem;
  disabled?: boolean;
};

export const NoteFormProvider = ({
  children,
  clientId,
  note,
  disabled = false
}: NoteFormProviderProps) => {
  const memoizedUuid = useMemo(() => uuidv4(), []);
  const sitesEnabled = useAppSelector((state) => state.clients.client.data?.enableProgramSites);
  const isBillingEnabled = useAppSelector((state) => state.clients.client.data?.enableBilling);

  const formik = useFormik<NoteFormValues>({
    initialValues: {
      note: note?.note || null,
      draft: note?.draft || true,
      noteType: note?.noteType || null,
      clientReached: note?.clientReached || null,
      dateOfEncounter: note?.dateOfEncounter || null,
      startTimeOfEncounter: note?.startTimeOfEncounter || null,
      nextContact: note?.nextContact || null,
      typeOfSupport: note?.typeOfSupport
        ? sortTypesOfSupportValues(note.typeOfSupport, typesOfSupportData)
        : [],
      id: note?.id || null,
      timeSpentMin: note?.timeSpentMin || null,
      siteId: note?.siteId || null,
      uuid: note?.uuid ?? memoizedUuid,
      zCodes:
        note?.selectedZCodes?.map((z) => ({
          id: z.id,
          name: `${z.code} - ${z.description}`,
          explanations: z.explanations,
          selectedExplanations: z.selectedExplanations?.map((e: any) => e.id.toString()) || []
        })) || [],
      whoDidYouReach: note?.whoDidYouReach || [],
      whoWereYouTryingToReach: note?.whoWereYouTryingToReach || '',
      howDidYouTryThem: note?.howDidYouTryThem || '',
      howDidYouReachThem: note?.howDidYouReachThem || null,
      whatTypeOfPlaceWereYouIn: note?.whatTypeOfPlaceWereYouIn || '',
      billingProviderId: note?.billingProviderId?.toString() || null,
      otherProviderName: note?.otherProviderName || '',
      documentationTime: note?.documentationTime || null,
      sitesEnabled: sitesEnabled,
      isBillingEnabled: isBillingEnabled,
      chwId: note?.chwId || null,
      managerId: note?.managerId || null
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: async () => {
      // Submit is handled by the Form component
    }
  });

  const isManagerReview = formik.values.noteType === 'MANAGER_REVIEW';

  const contextValue = useMemo(
    () => ({
      clientId,
      note,
      disabled,
      isManagerReview
    }),
    [clientId, note, disabled, isManagerReview]
  );

  return (
    <NoteFormContext.Provider value={contextValue}>
      <FormikProvider value={formik}>{children}</FormikProvider>
    </NoteFormContext.Provider>
  );
};
